<template>
  <div v-if="ppa">
    <RouterLink
      :to="{
        name: 'ComplianceGroup',
        params: { group: ppa.group },
        query: { view: 'incoming' },
      }"
      class="btn btn-sm btn-outline-secondary mb-3"
    >
      ← Incoming PPAs
    </RouterLink>
    <div class="d-flex align-items-center flex-wrap gap-3 mb-4">
      <h1 class="cg-page-title mb-0">{{ ppa.name }}</h1>
      <ToneBadge :tone="ppa.statusTone" :label="ppa.status" />
    </div>

    <SectionCard title="PPA overview">
      <div class="cg-kpis cg-rule-bottom">
        <StatBlock
          :value="fmt(ppa.total)"
          unit="MWh"
          caption="2026 forecast production"
          size="lg"
          captionAbove
        />
        <StatBlock
          :value="fmt(ppa.received)"
          :unit="`MWh · ${ppa.pct}`"
          caption="Delivered to date"
          size="lg"
          captionAbove
        />
        <StatBlock
          :value="fmt(ppa.stillExpected)"
          unit="MWh"
          caption="Still expected this year"
          size="lg"
          captionAbove
        />
      </div>
      <div class="cg-facts pt-3">
        <div>
          <p class="cg-micro mb-2">Agreement</p>
          <table class="table table-sm mb-0 cg-key-table">
            <tbody>
              <tr>
                <td class="text-body-secondary">Counterparty</td>
                <td>{{ ppa.seller }}</td>
              </tr>
              <tr>
                <td class="text-body-secondary">Country</td>
                <td>{{ ppa.country }}</td>
              </tr>
              <tr>
                <td class="text-body-secondary">Certificate system</td>
                <td>{{ ppa.system }}</td>
              </tr>
              <tr>
                <td class="text-body-secondary">Agreement ends</td>
                <td class="cg-tabular">{{ ppa.ends }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <p class="cg-micro mb-2">Delivery</p>
          <table class="table table-sm mb-0 cg-key-table">
            <tbody>
              <tr>
                <td class="text-body-secondary">Cadence</td>
                <td>{{ ppa.cadence }}</td>
              </tr>
              <tr>
                <td class="text-body-secondary">Method</td>
                <td>Transfer to account</td>
              </tr>
              <tr>
                <td class="text-body-secondary">Registry</td>
                <td>{{ ppa.registry }}</td>
              </tr>
              <tr>
                <td class="text-body-secondary">Contract terms</td>
                <td>{{ ppa.terms }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </SectionCard>

    <SectionCard title="Production devices">
      <div class="table-responsive">
        <table class="table table-sm mb-0 align-middle small">
          <thead>
            <tr>
              <th class="cg-micro">Device</th>
              <th class="cg-micro">Technology</th>
              <th class="cg-micro text-end">Delivered to date</th>
              <th class="cg-micro">Delivered through</th>
              <th class="cg-micro">Status</th>
            </tr>
          </thead>
          <tbody class="cg-tabular">
            <tr v-for="device in ppa.devices" :key="device.id">
              <td>
                <span class="fw-medium">{{ device.name }}</span>
                <span class="d-block text-body-secondary cg-mono">{{
                  device.id
                }}</span>
              </td>
              <td class="text-body-secondary">{{ device.tech }}</td>
              <td class="text-end fw-semibold">
                {{ fmt(device.delivered) }}
                <span class="text-body-secondary fw-normal">MWh</span>
              </td>
              <td class="text-body-secondary text-nowrap">
                {{ device.deliveredThrough }}
              </td>
              <td>
                <ToneBadge :tone="device.tone" :label="device.status" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </SectionCard>

    <SectionCard title="Production by month">
      <ProductionByMonthChart :bars="ppa.monthBars" :legend="ppa.monthLegend" />
    </SectionCard>

    <SectionCard title="Delivery history">
      <div class="table-responsive">
        <table class="table table-sm mb-0 align-middle small">
          <thead>
            <tr>
              <th class="cg-micro">Delivered on</th>
              <th class="cg-micro">Production period</th>
              <th class="cg-micro text-end">Volume</th>
              <th class="cg-micro">Production devices</th>
              <th class="cg-micro">Status</th>
            </tr>
          </thead>
          <tbody class="cg-tabular">
            <tr
              v-for="event in ppa.events"
              :key="`${event.state}-${event.date}-${event.period}`"
              :class="{ 'cg-row--overdue': event.state === 'overdue' }"
            >
              <td class="text-nowrap">
                <template v-if="event.state === 'overdue'"
                  >{{ event.daysLate }}
                  {{ event.daysLate === 1 ? "day" : "days" }} overdue</template
                >
                <template v-else>{{ event.date }}</template>
              </td>
              <td class="text-body-secondary text-nowrap">
                {{ event.period }}
              </td>
              <td class="text-end fw-semibold">{{ fmt(event.volume) }}</td>
              <td class="text-body-secondary">
                {{ event.devices.join(", ") }}
              </td>
              <td>
                <ToneBadge
                  :tone="EVENT_TONE[event.state]"
                  :label="EVENT_LABEL[event.state]"
                  :title="
                    event.state === 'overdue'
                      ? `Transfer was due ${event.date}`
                      : event.state === 'late'
                        ? `${event.daysLate} ${event.daysLate === 1 ? 'day' : 'days'} after the due date`
                        : undefined
                  "
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </SectionCard>

    <SectionCard>
      <PortfolioItemsTable
        :items="rows"
        :disableSelection="true"
        :hideActionBar="true"
        :defaultVisibleColumns="COLUMNS"
        storageContext="compliance-groups-ppa"
      >
        <template #header-start>
          <h2 class="cg-section-title">Certificates</h2>
        </template>
      </PortfolioItemsTable>
    </SectionCard>
  </div>
</template>

<script setup lang="ts">
import { toFlattenedPortfolioItems } from "@/compliance_groups/fixture/adapters";
import type { BadgeTone } from "@/compliance_groups/fixture/complianceGroupsTypes";
import type { DeliveryEvent } from "@/compliance_groups/fixture/derive";
import { fmt } from "@/compliance_groups/fixture/format";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import type { ColumnKey } from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { computed, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import ProductionByMonthChart from "./components/ProductionByMonthChart.vue";
import SectionCard from "./components/SectionCard.vue";
import StatBlock from "./components/StatBlock.vue";
import ToneBadge from "./components/ToneBadge.vue";
import {
  isGroupKey,
  isPpaKey,
  PPA_INPUTS,
} from "./fixture/complianceGroupsFixture";
import { useComplianceDemoStore } from "./useComplianceDemoStore";

const COLUMNS: ColumnKey[] = [
  "count",
  "technology_type",
  "production_month",
  "status",
  "portfolio_name",
  "registry_region",
  "production_device_name",
  "transfer_deadline",
];

const EVENT_TONE: Record<DeliveryEvent["state"], BadgeTone> = {
  received: "ok",
  late: "ok",
  overdue: "warn",
};
const EVENT_LABEL: Record<DeliveryEvent["state"], string> = {
  received: "Received",
  late: "Received late",
  overdue: "Overdue",
};

const route = useRoute();
const router = useRouter();
const store = useComplianceDemoStore();
// An agreement belongs to one group: unknown keys and cross-group URLs (rego/ppas/fr) both go
// back to the supply view of the group the agreement actually belongs to.
watch(
  () => [route.params.group, route.params.ppa] as const,
  ([group, ppaParam]) => {
    if (ppaParam === undefined) return;
    const owner = isPpaKey(ppaParam) ? PPA_INPUTS[ppaParam].group : null;
    if (owner === null || owner !== group) {
      router.replace({
        name: "ComplianceGroup",
        params: { group: owner ?? (isGroupKey(group) ? group : "eecs") },
        query: { view: "incoming" },
      });
    }
  },
  { immediate: true }
);
// Null until the URL names an agreement of this group; the watcher above redirects meanwhile,
// so no cross-group content is ever rendered, not even for a frame.
const ppa = computed(() => {
  const key = route.params.ppa;
  return isPpaKey(key) && PPA_INPUTS[key].group === route.params.group
    ? store.ppas[key]
    : null;
});
const rows = computed(() =>
  ppa.value ? toFlattenedPortfolioItems(ppa.value.lots) : []
);
</script>

<style scoped lang="scss">
.cg-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 16px;
}
.cg-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  column-gap: 40px;
  row-gap: 4px;
}
.cg-row--overdue > td {
  background: var(--phoenix-warning-bg-subtle);
}
</style>
